import type { SpellingRange } from "./check";
import type { CheckRequest, CheckResponse } from "./protocol";

export interface SpellingWorker {
	onmessage: ((event: MessageEvent<CheckResponse>) => void) | null;
	onerror: ((event: ErrorEvent) => void) | null;
	onmessageerror: ((event: MessageEvent) => void) | null;
	postMessage: (request: CheckRequest) => void;
	terminate: () => void;
}

interface Entry {
	listeners: Set<() => void>;
	ranges?: SpellingRange[];
}

/** One worker, one in-flight request, and a bounded cache shared by visible cells. */
export function createSpellingStore(
	createWorker: () => SpellingWorker,
	reportFailure: () => void,
	cacheLimit = 200,
) {
	const entries = new Map<string, Entry>();
	const cache = new Map<string, SpellingRange[]>();
	let worker: SpellingWorker | undefined;
	let active: CheckRequest | undefined;
	let timeout: ReturnType<typeof setTimeout> | undefined;
	let nextId = 0;
	let failed = false;
	let scheduled = false;

	function fail() {
		if (failed) return;
		failed = true;
		clearTimeout(timeout);
		worker?.terminate();
		worker = undefined;
		active = undefined;
		reportFailure();
	}

	function pump() {
		scheduled = false;
		if (failed || active) return;
		const next = [...entries].find(([, entry]) => !entry.ranges);
		if (!next) return;
		try {
			if (!worker) {
				worker = createWorker();
				worker.onerror = (event) => {
					event.preventDefault();
					fail();
				};
				worker.onmessageerror = fail;
				worker.onmessage = ({ data }) => {
					if (!active || data.id !== active.id) return;
					if ("error" in data) {
						fail();
						return;
					}
					clearTimeout(timeout);
					const text = active.text;
					active = undefined;
					cache.delete(text);
					cache.set(text, data.ranges);
					while (cache.size > cacheLimit) {
						const oldest = cache.keys().next().value;
						if (oldest === undefined) break;
						cache.delete(oldest);
					}
					const entry = entries.get(text);
					if (entry) {
						entry.ranges = data.ranges;
						for (const notify of entry.listeners) notify();
					}
					schedule();
				};
			}
			active = { id: ++nextId, text: next[0] };
			timeout = setTimeout(fail, 30_000);
			worker.postMessage(active);
		} catch {
			fail();
		}
	}

	function schedule() {
		if (scheduled || failed) return;
		scheduled = true;
		queueMicrotask(pump);
	}

	return {
		getSnapshot(text: string) {
			return entries.get(text)?.ranges ?? cache.get(text);
		},
		subscribe(text: string, notify: () => void) {
			if (!text || failed) return () => {};
			let entry = entries.get(text);
			if (!entry) {
				const ranges = cache.get(text);
				if (ranges) {
					cache.delete(text);
					cache.set(text, ranges);
				}
				entry = { listeners: new Set(), ranges };
				entries.set(text, entry);
			}
			entry.listeners.add(notify);
			schedule();
			return () => {
				entry.listeners.delete(notify);
				if (!entry.listeners.size) entries.delete(text);
			};
		},
	};
}
